import React, { useEffect } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { BRAND_GRADIENT } from '@presentation/styling/gradients';
import { Trans } from 'react-i18next';
import { format } from 'date-fns';
import InfiniteScroll from 'react-infinite-scroller';
import { ContentSection } from '@presentation/components/ContentSection';
import { CardsCarousel } from '@presentation/components/CardsCarusel';
import { ArtworkBanner } from '@presentation/components/ArtworkBanner';
import type { HomepageService } from '@domain/usecases/homepage/HomepageService';
import type { ContentItemWithAuthorModel } from '@domain/models';
import { useShowContentDetailsModalBasedOnUrl } from '@presentation/pages/Home/hooks/useShowContentDetailsModalBasedOnUrl';
import { useGetHomePageSection } from '@presentation/pages/Home/hooks/useGetHomePageSection';
import { SectionLoader } from '@presentation/components/Loader';
import { Outlet } from 'react-router-dom';
import { useSmartBanner } from '@presentation/context/SmartBannerContextProvider';
import Facebook from '@presentation/components/Facebook/Facebook';
import type { HomepageCategoryItem } from '@domain/usecases/config/ConfigRepository';

const HomePage = ({
	homepageService,
	collections,
	dailyPuzzle,
	showcaseImage,
}: {
	homepageService: HomepageService;
	collections: HomepageCategoryItem[];
	dailyPuzzle: ContentItemWithAuthorModel | null;
	showcaseImage: string | null;
}) => {
	const { generateLink } = useSmartBanner();

	const { sections, hasMore, fetchContent } = useGetHomePageSection(homepageService, collections);

	useEffect(() => {
		if (window && window.location.pathname === '/') {
			generateLink('HOME');
		}
	}, [generateLink]);

	/**
	 * Handles showing modal /puzzle/:id is visited directly, show a modal on this page.
	 */
	useShowContentDetailsModalBasedOnUrl();

	return (
		<>
			<Facebook />
			<ArtworkBanner
				isLoading={false}
				id={dailyPuzzle?.id || ''}
				title={dailyPuzzle?.name || ''}
				description={format(new Date(), 'do MMMM yyyy')}
				imageSrc={dailyPuzzle?.imageSrc || ''}
				backgroundImageSrc={dailyPuzzle?.imageSrcLowQuality || ''}
				creator={dailyPuzzle?.author || null}
			/>
			<Container maxWidth='xl' disableGutters>
				<Box
					sx={{
						position: 'relative',
						overflow: 'hidden',
						my: { xs: 2, md: 3 },
						px: { xs: 3, md: 6 },
						py: { xs: 4, md: 5 },
						borderRadius: '24px',
						border: '1px solid rgba(0, 0, 0, 0.06)',
						background: BRAND_GRADIENT,
					}}
				>
					{showcaseImage && (
						<Box
							aria-hidden='true'
							sx={{
								display: { xs: 'none', md: 'block' },
								position: 'absolute',
								top: 0,
								bottom: 0,
								right: 0,
								width: { md: '52%', lg: '46%' },
								overflow: 'hidden',
								pointerEvents: 'none',
								userSelect: 'none',
								// Clean fade from the band's gradient into the artwork.
								maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 55%, #000 100%)',
								WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 55%, #000 100%)',
							}}
						>
							<Box
								component='img'
								src={showcaseImage}
								alt=''
								loading='lazy'
								decoding='async'
								sx={{
									position: 'absolute',
									// Over-size the image so the 4° tilt can never expose a corner.
									top: '-12%',
									left: '-12%',
									width: '124%',
									height: '124%',
									objectFit: 'cover',
									transform: 'rotate(4deg)',
									transformOrigin: 'center',
								}}
							/>
						</Box>
					)}
					<Box sx={{ position: 'relative', zIndex: 1, maxWidth: { md: '60%' } }}>
						<Typography
							component='h2'
							sx={{
								fontSize: { xs: 22, md: 30 },
								fontWeight: 800,
								mb: 1.5,
								color: 'text.primary',
								textWrap: 'balance',
							}}
						>
							<Trans i18nKey='screens.home.seoHeading' />
						</Typography>
						<Typography
							component='h3'
							sx={{
								fontSize: { xs: 15, md: 17 },
								fontWeight: 400,
								lineHeight: 1.7,
								color: 'text.primary',
								maxWidth: '60ch',
								textWrap: 'balance',
							}}
						>
							<Trans i18nKey='screens.home.seoText' />
						</Typography>
					</Box>
				</Box>
			</Container>
			<InfiniteScroll
				pageStart={0}
				initialLoad={false}
				loadMore={() => fetchContent()}
				hasMore={hasMore}
				loader={<SectionLoader key='loader' />}
			>
				{sections.map((section) => (
					<ContentSection
						key={section.id}
						title={section.title}
						exploreAllLink={!section.isDynamic ? `/category/${section.id}` : undefined}
					>
						<CardsCarousel items={section.items} />
					</ContentSection>
				))}
			</InfiniteScroll>
			<Outlet />
		</>
	);
};

export default HomePage;
